import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardActionArea,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Cancel, ExpandMore, MoreVert, Save } from "@mui/icons-material";
import CardAgendamento from "../cardAgendamento";
import { useMemo, useState } from "react";
import Carousel from "../../easyMaps/carousel";
import { styles } from "./styles";
import AddReportModal from "./addReportModal";
import MenuGroup from "./menu";
import {
  IAgendamento,
  AgendamentoGroup,
} from "../../../pages/easyReport/agendamento";
import api from "../../../services/api";
import { notifyOnPage } from "../../../utils/notifyOnPage";
import AlertModal from "../../alertModal";

interface GroupsReportProps {
  grupos: AgendamentoGroup[];
  agendamentos: IAgendamento[];
  setGrupos: React.Dispatch<React.SetStateAction<AgendamentoGroup[]>>;
}

export default function GroupsReport({
  grupos,
  agendamentos,
  setGrupos,
}: GroupsReportProps) {
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openAddReportModal, setOpenAddReportModal] = useState(false);
  const [selectedGroup, setSelectGroup] = useState<AgendamentoGroup | null>(
    null,
  );
  const [deleteArray, setDeleteArray] = useState<string[]>([]);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  const idsToDelete = useMemo(
    () => new Set(deleteArray.map((item) => item)),
    [deleteArray],
  );

  const filteredArray = useMemo(() => {
    return grupos.map((group) => {
      if (group.id !== editingGroupId) {
        return group;
      }
      return {
        ...group,
        relatorios: group.relatorios.filter((r) => !idsToDelete.has(r.id)),
      };
    });
  }, [grupos, idsToDelete, editingGroupId]);
  const handleAddRelatorio = (
    id: string,
    titulo: string,
    relatorios: IAgendamento[],
  ) => {
    setSelectGroup({ id, titulo, relatorios });
    setOpenAddReportModal(true);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRename = async (newName: string) => {
    try {
      const response = await api.put("/easyReport/v2/editGroup", {
        id: selectedGroup?.id,
        titulo: newName,
      });
      if (response.data.status === "Error") {
        notifyOnPage(
          window.location.pathname,
          "Erro ao renomear título do grupo!",
          "error",
          3000,
        );
      } else {
        if (response.data) {
          notifyOnPage(
            window.location.pathname,
            "Título renomeado com sucesso!",
            "success",
            3000,
          );
          setGrupos((prevGrupos) =>
            prevGrupos.map((g) =>
              g.id === response.data.id ? response.data : g,
            ),
          );
        }
      }
    } catch (error: any) {
      console.error(error);
      notifyOnPage(
        window.location.pathname,
        error?.response?.data?.error || "Erro ao renomear título do grupo!",
        "error",
        3000,
      );
    }
  };

  const handleDeleteRelatorioToGroup = async () => {
    if (!deleteArray) return;

    try {
      const response = await api.put("/easyReport/v2/editGroup", {
        id: selectedGroup?.id,
        relatorioIds: selectedGroup?.relatorios
          .filter((r) => !deleteArray.includes(r.id))
          .map((r) => r.id),
      });
      if (response.data.status === "Error") {
      } else {
        if (response.data) {
          notifyOnPage(
            window.location.pathname,
            "Grupo atualizado com sucesso!",
            "success",
            3000,
          );
          setGrupos((prevGrupos) =>
            prevGrupos.map((g) =>
              g.id === response.data.id ? response.data : g,
            ),
          );
        }
      }
    } catch (error: any) {
      console.error(error);
      notifyOnPage(
        window.location.pathname,
        error?.response?.data?.error || "Erro ao editar grupo!",
        "error",
        3000,
      );
    } finally {
      setEditingGroupId(null);
      setDeleteArray([]);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      const response = await api.post("/easyReport/v2/deleteGroup", {
        id: selectedGroup?.id,
      });
      if (response.data.status === "Error") {
        notifyOnPage(
          window.location.pathname,
          "Erro ao excluir grupo!",
          "error",
          3000,
        );
      } else {
        if (response.data) {
          notifyOnPage(
            window.location.pathname,
            "Grupo excluído com sucesso!",
            "success",
            3000,
          );
          setGrupos((prevGrupos) =>
            prevGrupos.filter((g) => g.id !== selectedGroup?.id),
          );
        }
      }
    } catch (error: any) {
      console.error(error);
      notifyOnPage(
        window.location.pathname,
        error?.response?.data?.error || "Erro ao excluir grupo!",
        "error",
        3000,
      );
    } finally {
      setDeleteModal(false);
    }
  };

  return (
    <>
      {filteredArray.map((group) => {
        const isThisGroupEditing = editingGroupId === group.id;

        return (
          <Accordion key={group.id} sx={styles.accordion} disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel1-content"
              sx={styles.sumary}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
                  {group.titulo}
                </Typography>
                {!isThisGroupEditing ? (
                  <IconButton
                    id="options-button"
                    onClick={(e) => {
                      setSelectGroup({
                        id: group.id,
                        titulo: group.titulo,
                        relatorios: group.relatorios,
                      });
                      handleClick(e);
                    }}
                    size="small"
                    sx={{ ml: 2 }}
                    aria-controls={open ? "account-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                  >
                    <MoreVert />
                  </IconButton>
                ) : (
                  <Stack direction={"row"}>
                    <Tooltip title={"Salvar"} arrow>
                      <IconButton
                        id="save-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (
                            Array.isArray(deleteArray) &&
                            deleteArray.length > 0
                          ) {
                            handleDeleteRelatorioToGroup();
                          } else {
                            setEditingGroupId(null);
                          }
                        }}
                      >
                        <Save color={"secondary"} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={"Cancelar"} arrow>
                      <IconButton
                        id="cancel-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteArray([]);
                          setEditingGroupId(null);
                        }}
                      >
                        <Cancel color={"primary"} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            </AccordionSummary>
            <Divider />
            <AccordionDetails>
              <Box sx={styles.boxCard}>
                <Card sx={styles.card}>
                  <CardActionArea
                    onClick={() => {
                      handleAddRelatorio(
                        group.id,
                        group.titulo,
                        group.relatorios,
                      );
                    }}
                    sx={styles.cardAction}
                  >
                    <Add sx={styles.icon} color="primary" />
                    <Typography textAlign={"center"}>
                      Adicionar relatório
                    </Typography>
                  </CardActionArea>
                </Card>
                <Carousel>
                  {group.relatorios.map((card) => (
                    <CardAgendamento
                      key={card.id}
                      {...card}
                      inGroup={true}
                      editMode={isThisGroupEditing}
                      setDeleteArray={setDeleteArray}
                      deleteArray={deleteArray}
                    />
                  ))}
                </Carousel>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
      {openAddReportModal && (
        <AddReportModal
          openAddReportModal={openAddReportModal}
          setOpenAddReportModal={setOpenAddReportModal}
          relatorios={agendamentos}
          selectedGroup={selectedGroup}
          allGroups={filteredArray}
          setGroups={setGrupos}
        />
      )}

      {anchorEl && (
        <MenuGroup
          open={open}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          handleClose={handleClose}
          selectedGroup={selectedGroup}
          onRenameGroup={(newName: string) => {
            handleRename(newName);
          }}
          setDeleteModal={setDeleteModal}
          setEditMode={() => {
            if (selectedGroup) {
              setEditingGroupId(selectedGroup.id);
              handleClose();
            }
          }}
        />
      )}
      <AlertModal
        openModal={deleteModal}
        setOpenModal={setDeleteModal}
        severity="delete"
        singleButton={false}
        handleButton={handleDeleteGroup}
        textContent={
          "Se prosseguir com a operação, o grupo de agendamento será movido para a lixeira e excluído permanentemente após 30 dias."
        }
        handleCancel={() => {
          setDeleteModal(false);
        }}
      />
    </>
  );
}
